import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AssetsModule } from '@libraries/assets';
import { BrowserSupportModule } from '@libraries/browser-support';
import { ComponentLibraryModule } from '@libraries/component-library';
import { DatadogModule } from '@libraries/datadog';
import { FeatureFlagsModule } from '@libraries/feature-flags';
import { GoogleAnalyticsModule } from '@libraries/google-analytics';
import { GoogleMapsAddressModule } from '@libraries/google-maps';
import { HotjarModule } from '@libraries/hotjar';
import { ManagerClientModule } from '@libraries/manager-client';
import { SecureUploaderClientModule } from '@libraries/secure-uploader-client';
import { SentryModule } from '@libraries/sentry';
import { ApplyV2ConfigModule, ImprovementsConfigModule } from '@libraries/shared-config';
import { SharedEnvModule } from '@libraries/shared-env';
import { TranslocoHttpLoader, TranslocoRootModule } from '@libraries/transloco-root';
import { YodleeModule } from '@libraries/yodlee';
import { TRANSLOCO_LOADER } from '@jsverse/transloco';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CheckQuestionUrlPrefilledGuard } from './guards/check-questions-url-prefilled.guard';
import { AuthTokenInterceptor, CleanCognitoInterceptor } from './interceptors';
import { PrequalQuestionsModule } from './prequal-questions/prequal-questions.module';
import { effects, reducers } from './state';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    AssetsModule,
    BrowserAnimationsModule,
    BrowserSupportModule,
    ComponentLibraryModule,
    DatadogModule.forRoot({
      env: environment.envName,
      clientToken: environment.datadog.clientToken,
      applicationId: environment.datadog.applicationId,
      service: environment.appName,
    }),
    EffectsModule.forRoot(effects),
    FeatureFlagsModule,
    HotjarModule.forRoot({ siteId: environment.hotjarSiteId }),
    ManagerClientModule,
    PrequalQuestionsModule,
    ScrollToModule.forRoot(),
    SecureUploaderClientModule.forRoot({
      secureUploaderServiceUrl: environment.secureUploaderServiceUrl,
    }),
    YodleeModule.forRoot({
      fastlinkUrl: environment.yodlee.fastlinkUrl,
      secureUploaderServiceUrl: environment.secureUploaderServiceUrl,
    }),
    ImprovementsConfigModule.forRoot({
      managerUrl: '',
      apiUrl: '',
      maxContractConfig: { key: '', iv: '' },
    }),
    ApplyV2ConfigModule.forRoot({
      apiUrl: environment.managerClientConfig.baseUrl,
      envName: environment.envName,
      ownerLoginUrl: environment.redirectUrl,
      isPrequalificationApp: true,
    }),
    SentryModule.forRoot({
      environment: environment.envName,
      dsn: environment.sentryDsn,
    }),
    SharedEnvModule.forRoot(environment),
    StoreModule.forRoot(reducers),
    StoreRouterConnectingModule.forRoot(),
    TranslocoRootModule,
    // Below modules need to be included last because they depend on above StoreModule
    GoogleAnalyticsModule.forRoot({
      trackingId: environment.googleAnalyticsTrackingId,
    }),
    GoogleMapsAddressModule.forRoot({
      apiKey: environment.gmapsApiKey,
      libraries: ['places'],
    }),
    !environment.production || environment.envName === 'acceptance'
      ? StoreDevtoolsModule.instrument({ connectInZone: true })
      : [],
  ],
  providers: [
    CookieService,
    CheckQuestionUrlPrefilledGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CleanCognitoInterceptor,
      multi: true,
    },
    { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
